import useFetch from "hooks/useFetch";
import useRouteChange from "hooks/useRouteChange";
import { useEffect, useRef, useState } from "react";
const { content } = require("./../../config.json");

const localeRegExp = /^\/([a-z]{2}((_|-)[A-z]{2})?)(\/|$)/;
const cleanUrlExp = /[\\/#]+$/g;
const trailingSlashExp = /\/$/;

export const removeTrailingHash = (path) => path.replace(cleanUrlExp, "");
export const removeTrailingSlash = (path) => path.replace(trailingSlashExp, "");
const removeLocale = (path) => {
	const match = path.match(localeRegExp);
	if (match) {
		const locale = match[1];
		const remainingPath = path.replace(localeRegExp, "/");
		return { locale, remainingPath };
	}
	return { locale: null, remainingPath: path };
};

export const extractCanonical = (path) => {
	let cleanedPath = removeTrailingHash(path.split(/[#?]/)[0]);
	const lastSegment = cleanedPath.substring(cleanedPath.lastIndexOf("/") + 1);
	if (/=|,|^_/.test(lastSegment)) {
		cleanedPath = cleanedPath.replace(lastSegment, "");
	}
	cleanedPath = removeTrailingSlash(removeTrailingHash(cleanedPath));
	return cleanedPath;
};

const Seo = () => {
	const canonicalTagRef = useRef(null);
	const robotsTagRef = useRef(null);

	const [seoData, setSeoData] = useState([]);
	const [override, setOverride] = useState(null);
	const [updatedPath, setUpdatedPath] = useState("");

	const { pathname, hash, origin } = window.location;
	const { locale: extractedLocale, remainingPath } = removeLocale(pathname);
	const extractedPath = extractCanonical(`${remainingPath}${hash}`);

	const handleRouteChange = (path) => setUpdatedPath(path);
	useRouteChange(handleRouteChange);

	const { response } = useFetch(content.apiUrl.seoOverrides, {}, 60);

	useEffect(() => {
		setSeoData(response ? response.seoOverrides : []);
	}, [response]);

	useEffect(() => {
		let canonicalTag = document.querySelector("link[rel='canonical']");
		if (!canonicalTag) {
			canonicalTag = document.createElement("link");
			canonicalTag.setAttribute("rel", "canonical");
			document.head.appendChild(canonicalTag);
		}
		canonicalTagRef.current = canonicalTag;

		let robotsTag = document.querySelector("meta[name='robots']");
		if (!robotsTag) {
			robotsTag = document.createElement("meta");
			robotsTag.setAttribute("name", "robots");
			document.head.appendChild(robotsTag);
		}
		robotsTagRef.current = robotsTag;
	}, []);

	useEffect(() => {
		if (extractedPath && seoData) {
			const seoOverride = seoData.find(
				(data) => removeTrailingSlash(data.path) === extractedPath
			);
			if (seoOverride) {
				setOverride(seoOverride);
			} else {
				seoData.forEach((data) => {
					const basePath = data.path.replace("/*", "");
					if (data.path.endsWith("/*") && extractedPath.startsWith(basePath)) {
						setOverride(data);
					}
				});
			}
		}
	}, [extractedPath, seoData, updatedPath]);

	useEffect(() => {
		if (canonicalTagRef.current) {
			const canonicalUrl = extractedLocale
				? `${origin}/${extractedLocale}${removeTrailingHash(
					override?.canonical || extractedPath
				)}/`
				: `${origin}${removeTrailingHash(override?.canonical || extractedPath)}/`;
			canonicalTagRef.current.setAttribute("href", canonicalUrl);

			const normalizedCanonicalUrl = removeTrailingSlash(canonicalUrl);
			const normalizedBrowserUrl = removeTrailingSlash(window.location.href);

			if (robotsTagRef.current) {
				if (override?.robots) {
					robotsTagRef.current.setAttribute("content", override.robots);
				} else {
					if (normalizedCanonicalUrl !== normalizedBrowserUrl) {
						robotsTagRef.current.removeAttribute("content");
					} else {
						robotsTagRef.current.setAttribute("content", "index, follow");
					}
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [extractedPath, override]);

	return null;
};

export default Seo;
